import type { FieldMeta } from './resource'

/**
 * The life of a document (waybill, receipt, invoice, charter job), as in Frappe:
 *
 *   draft      saved and still editable or deletable; no document number yet
 *   submitted  final: numbered, has its effects (a paid waybill, a billed job); opens read-only,
 *              and "edit after submit" changes its `allowOnSubmit` fields (all of them by
 *              default), every change logged
 *   cancelled  kept for the record, effects undone; `amend` copies it into a new draft
 *
 * Business statuses (in transit, paid…) live in each resource's own `status` field.
 */
export const docStatuses = ['draft', 'submitted', 'cancelled'] as const
export type DocStatus = (typeof docStatuses)[number]

/** Permission actions every submittable resource gets */
export const docActions = ['submit', 'cancel', 'amend'] as const
export type DocAction = (typeof docActions)[number]

export interface SubmittableConfig {
  /**
   * Fields "edit after submit" may change (Frappe's "allow on submit"): `'all'` (the default)
   * or a list, e.g. `['notes']`. What other documents depend on is guarded by the server.
   */
  allowOnSubmit?: readonly string[] | 'all'
  /** Cancelling must give a reason (tax documents, receipts) */
  cancelReason?: 'optional' | 'required'
}

export type Submittable = Required<SubmittableConfig>

/** Fields the server keeps on every submittable record; they are not part of the input schema */
export const docFields = {
  docstatus: {
    type: 'select',
    options: docStatuses,
    optionLabels: 'doc.status',
    tones: { draft: 'neutral', submitted: 'info', cancelled: 'danger' },
    filter: true,
    label: 'doc.fields.docstatus',
    list: { width: 120, priority: 3, hidden: true },
  },
  amendedFrom: { label: 'doc.fields.amendedFrom', list: false },
  submittedAt: { type: 'datetime', label: 'doc.fields.submittedAt', list: { hidden: true, priority: 3 } },
  submittedBy: { label: 'doc.fields.submittedBy', list: { hidden: true, priority: 3 } },
  cancelledAt: { type: 'datetime', label: 'doc.fields.cancelledAt', list: false },
  cancelledBy: { label: 'doc.fields.cancelledBy', list: false },
  cancelReason: { label: 'doc.fields.cancelReason', list: false },
} satisfies Record<string, FieldMeta>

export const docFieldNames = Object.keys(docFields) as (keyof typeof docFields)[]

/** Records written before the lifecycle existed count as submitted */
export function docStatusOf(record: Readonly<Record<string, unknown>> | undefined): DocStatus {
  const value = record?.docstatus
  return docStatuses.includes(value as DocStatus) ? (value as DocStatus) : 'submitted'
}

/**
 * What the record's state allows, before permissions: edit and delete drafts,
 * submit drafts, cancel submitted documents, amend cancelled ones.
 */
export function lifecycleAllows(status: DocStatus, action: 'update' | 'delete' | DocAction): boolean {
  switch (action) {
    case 'update':
    case 'delete':
    case 'submit':
      return status === 'draft'
    case 'cancel':
      return status === 'submitted'
    case 'amend':
      return status === 'cancelled'
  }
}

/** Whether a field of a document may still be changed: anything on a draft, `allowOnSubmit` once submitted */
export function fieldEditable(submittable: Submittable | undefined, status: DocStatus, field: string): boolean {
  if (!submittable || status === 'draft') return true
  return status === 'submitted' && (submittable.allowOnSubmit === 'all' || submittable.allowOnSubmit.includes(field))
}

/** The input fields "edit after submit" may change */
export function fieldsEditableAfterSubmit(resource: { submittable?: Submittable; schema: { shape: object } }): string[] {
  const allow = resource.submittable?.allowOnSubmit
  if (!allow) return []
  return allow === 'all' ? Object.keys(resource.schema.shape) : [...allow]
}
